import { Checkbox, Flex } from 'antd';
import { useState } from 'react';

import { LoginId } from '@suntex.fim/api-client';
import { useFramework } from '@suntex.fim/framework';
import { Button, Tooltip } from '@suntex.fim/ui';
import { EmailOpened, TrashBlue } from '@suntex.fim/ui/icons';

import { DeleteMessageModal } from '../DeleteMessageModal/DeleteMessageModal';

type MessageListToolProps = {
  handleAllSelect: (checked: boolean) => void;
  allSelected: boolean;
  selectedMessages: Record<number, boolean>;
  /** Called after API mark-as-read so the parent can update local unread state. */
  onMarkedAsRead: (notificationId: number) => void;
  deleteMessage: (
    notificationIds: number | number[],
    loginId: LoginId,
  ) => Promise<void>;
  loginId: LoginId;
  mode: string;
  canDelete?: boolean;
};

export function MessageListTool({
  handleAllSelect,
  allSelected,
  selectedMessages,
  onMarkedAsRead,
  deleteMessage,
  loginId,
  mode,
  canDelete = true,
}: MessageListToolProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { api } = useFramework();

  const markAsRead = async (notificationId: number) => {
    await api.post_Notification_MarkRead({ notificationId });
    onMarkedAsRead(notificationId);
  };

  const handleSelectedMarkAsRead = async () => {
    await Promise.all(
      Object.entries(selectedMessages)
        .filter(([, selected]) => selected)
        .map(([key]) => markAsRead(Number(key))),
    );
  };

  const handleOk = async () => {
    await deleteMessage(
      Object.entries(selectedMessages)
        .filter(([, selected]) => selected)
        .map(([key]) => Number(key)),
      loginId,
    );
    setIsModalOpen(false);
  };

  const hasSelectedMessages = Object.values(selectedMessages).some(Boolean);

  return (
    <Flex className="message-list-tool">
      <div className="message-list-tool__indicator">
        <Checkbox
          checked={allSelected}
          onChange={(e) => handleAllSelect(e.target.checked)}
          aria-label="Select all messages"
        />
        Select All
      </div>

      {hasSelectedMessages && (
        <Flex className="message-list-tool__action" align="center">
          {mode === 'inbox' && (
            <Tooltip title="Mark as Read">
              <Button
                aria-label="Mark as Read"
                shape="round"
                intent="icon"
                size="small"
                onClick={() => void handleSelectedMarkAsRead()}
              >
                <EmailOpened />
              </Button>
            </Tooltip>
          )}
          {canDelete && (
            <>
              <Tooltip title="Delete">
                <Button
                  aria-label="Delete"
                  shape="round"
                  intent="icon"
                  size="small"
                  onClick={() => setIsModalOpen(true)}
                >
                  <TrashBlue />
                </Button>
              </Tooltip>
              <DeleteMessageModal
                isOpen={isModalOpen}
                title="Delete Message"
                onOk={() => void handleOk()}
                onCancel={() => setIsModalOpen(false)}
                content={
                  <>
                    <p className="fw-bold">
                      Are you sure you want to delete this message?
                    </p>
                    <p>
                      Once you delete the message it will be completely removed
                      from the platform.
                    </p>
                  </>
                }
              />
            </>
          )}
        </Flex>
      )}
    </Flex>
  );
}
